import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getHHActiveVacancies, getHHVacancyDetail } from '../../../relevanter/services/api';
import toast from 'react-hot-toast';
import { useBrandingColors } from '../../../hooks/useBrandingColors';
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import voiceCancelIcon from '../../../assets/icons/voice-cancel.png';
import voiceConfirmIcon from '../../../assets/icons/voice-confirm.png';
import importHhButton from '../../../assets/icons/import-hh-button.png';
import sendButtonIcon from '../../../assets/icons/send-button.png';
import sendButtonLoadingIcon from '../../../assets/icons/send-button-loading.png';
import sendButtonDisabledIcon from '../../../assets/icons/send-button-disabled.png';
import { ThinkingAnimation } from './ThinkingAnimation';
import { FieldProgressList } from './FieldProgressList';
import { VacancyOverview } from './VacancyOverview';
import type { ChatMessage, VacancyFieldStatus } from './types';
import aiSunIcon from '../../../assets/icons/ai-sun-icon.png';

interface AiAssistantPanelProps {
  messages: ChatMessage[];
  isGenerating: boolean;
  onSendMessage: (text: string) => void;
  onReset: () => void;
  onCollapse: () => void;
  onFieldClick?: (fieldKey: string) => void;
  onImportFromHH?: (hhVacancyId: string) => void;
  /** Fires when user picks an HH vacancy FROM THE LIST of their published vacancies —
   * parent should set it as the position's main HH vacancy so dialogs/responses get
   * attached. НЕ вызывается при импорте по URL (ссылка может быть чужой вакансией,
   * автоматически линковать нельзя). */
  onSelectMainHhVacancy?: (hhVacancyId: string) => void;
}

export function AiAssistantPanel({
  messages,
  isGenerating,
  onSendMessage,
  onReset,
  onCollapse,
  onFieldClick,
  onImportFromHH,
  onSelectMainHhVacancy,
}: AiAssistantPanelProps) {
  const colors = useBrandingColors();
  const [inputText, setInputText] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showHHModal, setShowHHModal] = useState(false);
  const [hhVacancies, setHhVacancies] = useState<Array<{ id: string; name: string; area?: { name: string } }>>([]);
  const [hhLoading, setHhLoading] = useState(false);
  const [hhLink, setHhLink] = useState('');
  const [hhImporting, setHhImporting] = useState(false);
  const [hhPendingVacancyId, setHhPendingVacancyId] = useState<string | null>(null);
  const [showHHResetConfirm, setShowHHResetConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const onSpeechResult = useCallback((text: string) => {
    setInputText(text);
  }, []);

  const { isListening, audioLevel, startListening, stopListening, isSupported: isSpeechSupported } =
    useSpeechRecognition(onSpeechResult);

  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
    prevMsgCountRef.current = messages.length;
  }, [messages]);

  const handleOpenHHImport = useCallback(async () => {
    setShowHHModal(true);
    setHhLink('');
    setHhLoading(true);
    try {
      const response = await getHHActiveVacancies();
      const items = response?.vacancies?.items || response?.items || [];
      setHhVacancies(Array.isArray(items) ? items : []);
    } catch {
      setHhVacancies([]);
    } finally {
      setHhLoading(false);
    }
  }, []);

  // Источник импорта: 'list' — клик по своей опубликованной вакансии; 'link' — URL.
  // Нужно различать, т.к. для list подтягиваем как "Основную вакансию" (своя, доверенная),
  // а для link — не линкуем автоматически (ссылка может быть чужой).
  const [hhPendingSource, setHhPendingSource] = useState<'list' | 'link'>('list');

  const startHHImport = useCallback((vacancyId: string, source: 'list' | 'link' = 'list') => {
    if (messages.length > 0) {
      setHhPendingVacancyId(vacancyId);
      setHhPendingSource(source);
      setShowHHResetConfirm(true);
    } else {
      importHHVacancy(vacancyId, source);
    }
  }, [messages.length]);

  const confirmHHImport = useCallback(() => {
    setShowHHResetConfirm(false);
    if (hhPendingVacancyId) {
      importHHVacancy(hhPendingVacancyId, hhPendingSource);
      setHhPendingVacancyId(null);
    }
  }, [hhPendingVacancyId, hhPendingSource]);

  const importHHVacancy = useCallback(async (vacancyId: string, source: 'list' | 'link' = 'list') => {
    setHhImporting(true);
    setShowHHModal(false);
    try {
      const response = await getHHVacancyDetail(vacancyId);
      const data = response?.vacancyDetail;
      if (!data) {
        toast.error('Не удалось загрузить данные вакансии с HH.ru');
        return;
      }
      // Build a text summary from HH data and send it through the AI assistant
      const parts: string[] = [];
      if (data.title) parts.push(data.title);
      if (data.description) {
        const cleanDesc = data.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        parts.push(cleanDesc);
      }
      if (data.skills?.length) parts.push(`Навыки: ${data.skills.join(', ')}`);
      if (data.salaryFrom || data.salaryTo) {
        const from = data.salaryFrom ? `от ${data.salaryFrom}` : '';
        const to = data.salaryTo ? `до ${data.salaryTo}` : '';
        const cur = data.salaryCurrency || 'RUR';
        parts.push(`Зарплата: ${[from, to].filter(Boolean).join(' ')} ${cur}`);
      }
      if (data.cities?.length) parts.push(`Город: ${data.cities.join(', ')}`);
      if (data.workFormat) parts.push(`Формат: ${data.workFormat}`);
      if (data.workSchedule) parts.push(`График: ${data.workSchedule}`);
      if (data.totalExperience) parts.push(`Опыт: ${data.totalExperience}`);
      if (Array.isArray(data.employmentContractTypes) && data.employmentContractTypes.length > 0) {
        const contractLabels: Record<string, string> = {
          tk_rf: 'По ТК',
          ip: 'ИП',
          self_employed: 'Самозанятый',
          individual: 'ГПХ',
          accredited_it: 'Аккредитованная IT',
        };
        const labels = data.employmentContractTypes
          .map((code: string) => contractLabels[code] || code)
          .join(', ');
        parts.push(`Оформление: ${labels}`);
      }

      // Reset context before importing to avoid mixing vacancies
      onReset();
      // Small delay to let reset complete before sending new message
      await new Promise(resolve => setTimeout(resolve, 100));
      const prompt = `[Импорт из HH.ru] ${parts.join('. ')}`;
      onSendMessage(prompt);
      // Set this vacancy as "основная вакансия" ТОЛЬКО когда импорт из списка своих
      // опубликованных HH-вакансий. При импорте по URL ссылка может быть чужой —
      // не линкуем автоматически, пользователь выберет сам если нужно.
      if (source === 'list' && onSelectMainHhVacancy) {
        onSelectMainHhVacancy(vacancyId);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || 'Ошибка при импорте с HH.ru';
      toast.error(msg);
    } finally {
      setHhImporting(false);
    }
  }, [onSendMessage, onReset]);

  const handleHHLinkImport = useCallback(() => {
    // Extract vacancy ID from HH URL
    const match = hhLink.match(/vacancy\/(\d+)/);
    if (!match) {
      toast.error('Неверная ссылка. Вставьте ссылку вида hh.ru/vacancy/12345');
      return;
    }
    startHHImport(match[1], 'link');
  }, [hhLink, startHHImport]);

  const handleSend = () => {
    if (!inputText.trim() || isGenerating) return;
    if (isListening) stopListening();
    onSendMessage(inputText.trim());
    setInputText('');
  };

  const handleMicClick = () => {
    if (isListening) return;
    setInputText('');
    startListening();
  };

  const handleVoiceConfirm = () => {
    stopListening();
    if (inputText.trim()) {
      setIsTranscribing(true);
      // Brief "transcribing" state, then back to normal with text ready
      setTimeout(() => setIsTranscribing(false), 500);
    }
  };

  const handleVoiceCancel = () => {
    stopListening();
    setInputText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2e4e9] shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={onCollapse}
            className="text-[#525866] hover:text-[#0a0d14] transition-colors"
            title="Свернуть"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 4L10 8L14 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="text-[14px] font-semibold text-[#0a0d14]">
            <span style={{ color: colors.primary }}>AI</span> Ассистент
          </span>
        </div>
        <button
          onClick={() => setShowResetConfirm(true)}
          className="flex items-center gap-1 text-[13px] text-[#0a0d14] hover:opacity-70 transition-opacity"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 8C2 9.18669 2.35189 10.3467 3.01118 11.3334C3.67047 12.3201 4.60754 13.0892 5.7039 13.5433C6.80026 13.9974 8.00666 14.1162 9.17054 13.8847C10.3344 13.6532 11.4035 13.0818 12.2426 12.2426C13.0818 11.4035 13.6532 10.3344 13.8847 9.17054C14.1162 8.00666 13.9974 6.80026 13.5433 5.7039C13.0892 4.60754 12.3201 3.67047 11.3334 3.01118C10.3467 2.35189 9.18669 2 8 2C6.32263 2.00631 4.71265 2.66082 3.50667 3.82667L2 5.33333" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 2V5.33333H5.33333" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Сбросить
        </button>
      </div>

      {/* Messages area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4 custom-scrollbar">
        {/* Empty state */}
        {messages.length === 0 && !isGenerating && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <img src={aiSunIcon} alt="" className="w-14 h-14 mb-4" />
            <h3 className="text-[16px] font-semibold text-[#0a0d14] mb-2">Создайте вакансию с AI</h3>
            <p className="text-[14px] text-[#868C98] leading-relaxed">
              Опишите вакансию или импортируйте из HeadHunter, и AI автоматически заполнит все необходимые поля
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className="ai-fade-in">
            {msg.type === 'user' && (
              <div className="flex justify-end">
                <div
                  className="max-w-[85%] rounded-[16px] px-4 py-2.5 text-[14px] text-[#0a0d14]"
                  style={{ backgroundColor: '#F5F6F1' }}
                >
                  {msg.text}
                </div>
              </div>
            )}

            {msg.type === 'thinking' && (
              <ThinkingAnimation />
            )}

            {msg.type === 'progress' && msg.fields && (
              <FieldProgressList fields={msg.fields} />
            )}

            {msg.type === 'done' && (
              <div className="text-[14px] text-[#0a0d14] leading-relaxed">
                {msg.text}
              </div>
            )}

            {msg.type === 'assistant' && (
              <div className="text-[14px] text-[#0a0d14] leading-relaxed">
                {msg.text}
              </div>
            )}

            {msg.type === 'overview' && msg.fields && (
              <VacancyOverview fields={msg.fields} onFieldClick={onFieldClick} />
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="shrink-0 px-4 pb-4 pt-2">
        <div className="border border-[#e2e4e9] rounded-[12px] bg-white">
          {/* Recording mode */}
          {isListening ? (
            <div className="ai-fade-in">
              {/* "Слушаю..." text - same position as textarea placeholder */}
              <div className="px-4 pt-3 min-h-[90px]">
                <span className="text-[14px] text-[#868C98]">Слушаю...</span>
              </div>
              {/* Waveform + buttons row - same height as button row below textarea */}
              <div className="flex items-center gap-3 px-3 pb-2 h-[40px]">
                <div className="flex-1 flex items-center gap-[1.5px] h-5 overflow-hidden">
                  {Array.from({ length: 60 }).map((_, i) => {
                    const center = 30;
                    const distFromCenter = Math.abs(i - center) / center;
                    const speakHeight = audioLevel * (1 - distFromCenter * 0.8) * 18;
                    const height = 2 + Math.max(0, speakHeight);
                    const isRed = i < 15;
                    return (
                      <div
                        key={i}
                        className="rounded-full shrink-0 transition-all duration-75"
                        style={{
                          width: '2px',
                          height: `${height}px`,
                          backgroundColor: isRed ? colors.primary : '#0a0d14',
                          opacity: isRed ? 0.6 : 0.3,
                        }}
                      />
                    );
                  })}
                </div>
                {/* Cancel button */}
                <button
                  onClick={handleVoiceCancel}
                  className="shrink-0 hover:opacity-80 transition-opacity"
                  title="Отменить"
                >
                  <img src={voiceCancelIcon} alt="Отменить" className="w-9 h-9" />
                </button>
                {/* Confirm button */}
                <button
                  onClick={handleVoiceConfirm}
                  className="shrink-0 hover:opacity-80 transition-opacity"
                  title="Подтвердить"
                >
                  <img src={voiceConfirmIcon} alt="Подтвердить" className="w-9 h-9" />
                </button>
              </div>
            </div>
          ) : (
            <div className="ai-fade-in">
              {/* Normal / transcribing mode */}
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isTranscribing ? 'Идет расшифровка записи...' : 'Добавьте описание вакансии'}
                disabled={isGenerating || isTranscribing}
                className="w-full px-4 pt-3 pb-2 text-[14px] bg-transparent border-none outline-none resize-none text-[#0a0d14] placeholder-[#868C98] disabled:opacity-50 min-h-[80px] max-h-[160px]"
                rows={3}
              />
              <div className="flex items-center justify-between px-3 pb-2">
                <button
                  onClick={handleOpenHHImport}
                  className="flex items-center gap-1.5 text-[13px] text-[#0a0d14] border border-[#e2e4e9] rounded-[8px] px-3 py-1.5 bg-white hover:bg-[#f9fafb] transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Импорт из НН
                </button>
                <div className="flex items-center gap-2">
                  {/* Mic button */}
                  <button
                    onClick={handleMicClick}
                    disabled={!isSpeechSupported || isGenerating || isTranscribing}
                    className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-70 transition-opacity"
                    title="Голосовой ввод"
                  >
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" className="w-5 h-5">
                      <path d="M11.875 4.25C11.875 3.23438 11.0156 2.375 10 2.375C8.94531 2.375 8.125 3.23438 8.125 4.25V10.5C8.125 11.5547 8.94531 12.375 10 12.375C11.0156 12.375 11.875 11.5547 11.875 10.5V4.25ZM6.25 4.25C6.25 2.17969 7.92969 0.5 10 0.5C12.0703 0.5 13.75 2.17969 13.75 4.25V10.5C13.75 12.5703 12.0703 14.25 10 14.25C7.92969 14.25 6.25 12.5703 6.25 10.5V4.25ZM5 8.9375V10.5C5 13.2734 7.22656 15.5 10 15.5C12.7344 15.5 15 13.2734 15 10.5V8.9375C15 8.42969 15.3906 8 15.9375 8C16.4453 8 16.875 8.42969 16.875 8.9375V10.5C16.875 14.0156 14.2578 16.8672 10.9375 17.3359V18.625H12.8125C13.3203 18.625 13.75 19.0547 13.75 19.5625C13.75 20.1094 13.3203 20.5 12.8125 20.5H10H7.1875C6.64062 20.5 6.25 20.1094 6.25 19.5625C6.25 19.0547 6.64062 18.625 7.1875 18.625H9.0625V17.3359C5.70312 16.8672 3.125 14.0156 3.125 10.5V8.9375C3.125 8.42969 3.51562 8 4.0625 8C4.57031 8 5 8.42969 5 8.9375Z" fill="#0A0D14" />
                    </svg>
                  </button>
                  {/* Send button */}
                  <button
                    onClick={handleSend}
                    disabled={(!inputText.trim() && !isTranscribing) || isGenerating}
                    className={`shrink-0 transition-opacity ${inputText.trim() && !isTranscribing ? 'hover:opacity-80' : ''}`}
                    title="Отправить"
                  >
                    <img
                      src={isTranscribing ? sendButtonLoadingIcon : inputText.trim() ? sendButtonIcon : sendButtonDisabledIcon}
                      alt="Отправить"
                      className="w-8 h-8"
                    />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showResetConfirm && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
          onClick={() => setShowResetConfirm(false)}
        >
          <div
            className="bg-white rounded-[16px] shadow-xl p-6 w-[360px] ai-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[16px] font-medium text-[#0a0d14] mb-2">Сбросить контекст?</p>
            <p className="text-[14px] text-[#525866] mb-5">Чат будет очищен, а форма сброшена к исходному состоянию.</p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-[14px] font-medium text-[#525866] rounded-[10px] border border-[#e2e4e9] hover:bg-[#f9fafb] transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => { onReset(); setShowResetConfirm(false); }}
                className="px-4 py-2 text-[14px] font-medium text-white rounded-[10px] bg-[#e53935] hover:bg-[#c62828] transition-colors"
              >
                Сбросить
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showHHResetConfirm && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
          onClick={() => { setShowHHResetConfirm(false); setHhPendingVacancyId(null); }}
        >
          <div
            className="bg-white rounded-[16px] shadow-xl p-6 w-[400px] ai-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[16px] font-medium text-[#0a0d14] mb-2">Импорт вакансии</p>
            <p className="text-[14px] text-[#525866] mb-5">Импорт из HH.ru сбросит текущие данные вакансии. Продолжить?</p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => { setShowHHResetConfirm(false); setHhPendingVacancyId(null); }}
                className="px-4 py-2 text-[14px] font-medium text-[#525866] rounded-[10px] border border-[#e2e4e9] hover:bg-[#f9fafb] transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={confirmHHImport}
                className="px-4 py-2 text-[14px] font-medium text-white rounded-[10px] bg-[#D76958] hover:bg-[#c45a4a] transition-colors"
              >
                Продолжить
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showHHModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
          onClick={() => setShowHHModal(false)}
        >
          <div
            className="bg-white rounded-[16px] shadow-xl p-6 w-[480px] max-h-[80vh] flex flex-col ai-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[16px] font-medium text-[#0a0d14]">Импорт вакансии из HH.ru</h3>
              <button
                onClick={() => setShowHHModal(false)}
                className="text-[#868C98] hover:text-[#0a0d14] transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {/* Link input */}
            <div className="mb-5">
              <label className="text-[13px] font-medium text-[#525866] mb-1.5 block">Ссылка на вакансию</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={hhLink}
                  onChange={(e) => setHhLink(e.target.value)}
                  placeholder="https://hh.ru/vacancy/12345"
                  className="flex-1 px-3 py-2 text-[14px] border border-[#e2e4e9] rounded-[10px] outline-none focus:border-[#525866] transition-colors"
                  onKeyDown={(e) => e.key === 'Enter' && handleHHLinkImport()}
                />
                <button
                  onClick={handleHHLinkImport}
                  disabled={!hhLink.trim() || hhImporting}
                  className="px-4 py-2 text-[14px] font-medium text-white rounded-[10px] bg-[#D76958] hover:bg-[#c45a4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                >
                  Импорт
                </button>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-[#e2e4e9]" />
              <span className="text-[12px] text-[#868C98]">или выберите из черновиков</span>
              <div className="flex-1 h-px bg-[#e2e4e9]" />
            </div>

            {/* Drafts list */}
            <div className="flex-1 overflow-y-auto">
              {hhLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-[14px] text-[#868C98]">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Загрузка вакансий...
                  </div>
                </div>
              ) : hhVacancies.length === 0 ? (
                <div className="text-center py-8 text-[14px] text-[#868C98]">
                  Нет активных вакансий на HH.ru
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {hhVacancies.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      disabled={hhImporting}
                      onClick={() => startHHImport(v.id)}
                      className="w-full text-left px-4 py-3 rounded-[10px] border border-[#e2e4e9] hover:bg-[#f9fafb] hover:border-[#c5c8d0] transition-colors disabled:opacity-50"
                    >
                      <div className="text-[14px] font-medium text-[#0a0d14]">{v.name}</div>
                      {v.area?.name && (
                        <div className="text-[12px] text-[#868C98] mt-0.5">{v.area.name}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
